/**
 * V4.24.49 — CCRIS Score + Selection Path (single screen, per user spec-b).
 *
 * Combines the AICon score card (image 2) with the a/b/c/d selection path
 * picker (image 3) into one page. Score gate uses `aicon_bank_review_report.is_passed`:
 *   • is_passed = true  → show full a/b/c/d picker (Recommended = default)
 *   • is_passed = false → hide picker, show Special Handling banner and
 *                          "Continue as Special Handling" CTA (auto-Type-D)
 *
 * Deep-link path: `/loan/:id/score`.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Loader2, CheckCircle2, TrendingUp, User, AlertTriangle } from 'lucide-react';
import { LoanPageHeader } from './_shared';

const PATH_OPTIONS = [
  // V4.24.72 — Web now matches Mobile: only 3 loan-type options at
  // `ready_pick_banker`. Type D (Special Handling) is NOT an explicit
  // choice — it's automatically selected downstream if the AICon score
  // gate fails (see the "Continue as Special Handling" fallback below).
  { id: 'A_own_bank',    letter: 'a', title: "Customer's own bank", subtitle: 'No system action · manual update' },
  { id: 'B_outsource',   letter: 'b', title: 'Outsource agency',    subtitle: 'Send to a partner agency for processing' },
  { id: 'C_recommended', letter: 'c', title: 'Recommended bank',    subtitle: 'Best match based on your CCRIS profile', starred: true },
];

export default function LoanScoreAndPath() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState('C_recommended'); // Recommended by default
  const [submitting, setSubmitting] = useState(false);
  // V4.24.73 — Two-phase wizard:
  //   'aicon'  → show CCRIS/DSR/risk band + recommended banks + Continue
  //   'picker' → show 3 loan-type options (with inline outsource picker for Type B)
  const [phase, setPhase] = useState('aicon');
  // V4.24.73 — Outsource partner list loaded lazily when the user hits phase 2.
  const [outsourcePartners, setOutsourcePartners] = useState([]);
  const [selectedOutsourceId, setSelectedOutsourceId] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/v1/loan-cases/${id}`);
        setCaseData(r.data?.data || null);
      } catch (e) { toast.error('Failed to load case'); }
      finally { setLoading(false); }
    })();
  }, [id]);

  // Load outsource partners once when the picker phase opens.
  useEffect(() => {
    if (phase !== 'picker' || outsourcePartners.length > 0) return;
    (async () => {
      try {
        const r = await api.get('/public/v1/outsource-partners?limit=50');
        setOutsourcePartners(r.data?.data?.items || []);
      } catch (e) { /* non-blocking */ }
    })();
  }, [phase, outsourcePartners.length]);

  const submit = async (pathId) => {
    // V4.24.73 — Type B now selects the outsource partner inline (radio
    // list in phase 2). We call /select-outsource directly, which sets
    // the path + partner + advances status → `waiting_outsource`.
    if (pathId === 'B_outsource') {
      if (!selectedOutsourceId) {
        toast.error('Please select an outsource partner');
        return;
      }
      setSubmitting(true);
      try {
        await api.post(`/public/v1/loan-cases/${id}/select-outsource`, {
          outsource_partner_id: selectedOutsourceId,
        });
        toast.success('Outsource partner selected');
        navigate(`/loan/${id}`);
      } catch (e) {
        toast.error(e.response?.data?.detail?.message || 'Failed to select outsource partner');
      } finally { setSubmitting(false); }
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.patch(`/public/v1/loan-cases/${id}/path`, { selection_path: pathId });
      const ns = r.data?.data?.next_screen;
      toast.success(`Loan Type ${pathId[0].toUpperCase()} selected`);
      const routeMap = {
        loan_detail:            `/loan/${id}`,
        loan_recommended_banks: `/loan/${id}/pick-bank`,
        loan_offers:            '/loan-offers',
      };
      navigate(routeMap[ns] || `/loan/${id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'Failed to set path');
    } finally { setSubmitting(false); }
  };

  if (loading || !caseData) {
    return <div className="p-6 text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>;
  }

  // V4.24.80 — Read AICon data from the correct webhook fields:
  //   aicon_ccris_report  → { status: 'clean' | 'issues', clients: [...] }
  //   aicon_dsr_report    → { dsr_percent, dsr_limit_percent, is_passed, ndi, ... }
  //   aicon_band          → 'green' | 'amber' | 'red'
  //   aicon_bank_review_report → { suitable[], caution[], not_suitable[] }
  // The previous code assumed all these fields were on
  // `aicon_bank_review_report` (they never were) — resulting in a blank
  // "— / 1000 · Poor" score card for perfectly valid green cases.
  const ccrisReport = caseData.aicon_ccris_report || {};
  const dsrReport = caseData.aicon_dsr_report || {};
  const bankReview = caseData.aicon_bank_review_report || {};
  const aiconBand = (caseData.aicon_band || '').toLowerCase();

  const ccrisStatus = ccrisReport.status || null; // 'clean' | 'issues' | ...
  const ccrisClient = Array.isArray(ccrisReport.clients) && ccrisReport.clients.length > 0
    ? ccrisReport.clients[0]
    : null;
  const ccrisFacilities = ccrisClient?.summary?.facilities_count;
  const ccrisArrears = ccrisClient?.summary?.arrears_months_max;

  const dsrPctRaw = dsrReport.dsr_percent;
  const dsrPct = dsrPctRaw != null ? parseFloat(dsrPctRaw) : NaN;
  const dsrLimit = dsrReport.dsr_limit_percent != null ? parseFloat(dsrReport.dsr_limit_percent) : 70;
  const dsrIsPassed = dsrReport.is_passed;
  const dsrWithinLimit = typeof dsrIsPassed === 'boolean' ? dsrIsPassed : (Number.isFinite(dsrPct) && dsrPct <= dsrLimit);
  const formulatedIncome = dsrReport.formulated_income;
  const maxLoanAmount = dsrReport.max_loan_amount || bankReview.max_loan_amount || null;

  // V4.24.80 — Recommended banks: prefer `bank_review.suitable[]`
  // (webhook payload) then fall back to the normalised
  // `recommended_banks.suitable[]` structure the backend attaches.
  const recommendedBanks = (
    bankReview.suitable
    || caseData.recommended_banks?.suitable
    || []
  ).slice(0, 8).map((b) => ({
    bank_code: b.code || b.bank_code || b.name || b.bank_name,
    bank_name: b.name || b.bank_name || b.bank_code,
  }));

  // V4.24.79/80 — Picker visibility rules:
  //   - Ready Pick Bank and AICon did not explicitly fail (band != 'red',
  //     DSR is_passed != false) → show picker.
  //   - Waiting Score / Profiling → show "AICon is scoring" banner.
  //   - Explicit fail (dsr is_passed=false or aicon_band='red') → Special Handling.
  const isReadyPickBank = caseData.status === 'ready_pick_banker';
  const isExplicitFail = dsrIsPassed === false || aiconBand === 'red';
  const isPickerAvailable = isReadyPickBank && !isExplicitFail;
  const isWaitingScore = caseData.status === 'waiting_score' || caseData.status === 'profiling';

  const bandLabel = aiconBand ? aiconBand.charAt(0).toUpperCase() + aiconBand.slice(1) : '—';
  const bandColor = aiconBand === 'green' ? 'text-emerald-600'
    : aiconBand === 'amber' || aiconBand === 'yellow' ? 'text-amber-600'
    : aiconBand === 'red' ? 'text-red-600' : 'text-gray-600';
  const ccrisIsClean = (ccrisStatus || '').toLowerCase() === 'clean';

  return (
    <div className="max-w-3xl mx-auto p-3 sm:p-6 space-y-4" data-testid="loan-score-page">
      <LoanPageHeader caseData={caseData} title="Loan" />

      {/* V4.24.80 — Score card: CCRIS status text + DSR % + Risk Band,
          matches mobile design and reads from the correct AICon reports. */}
      <Card className="p-5 space-y-4" data-testid="ccris-score-card">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${ccrisIsClean ? 'bg-emerald-100' : 'bg-amber-100'}`}>
            <CheckCircle2 className={`w-5 h-5 ${ccrisIsClean ? 'text-emerald-600' : 'text-amber-600'}`} />
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-wider text-gray-500">CCRIS Score</p>
            <p className="font-bold text-lg capitalize" data-testid="ccris-score-value">
              {ccrisStatus || '—'}
            </p>
            <p className="text-xs text-gray-500">
              {ccrisStatus
                ? `${ccrisFacilities ?? 0} facilities · ${ccrisArrears ?? 0} arrears`
                : 'Credit score'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
            <span className="text-blue-700 text-xs font-bold">DSR</span>
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-wider text-gray-500">DSR</p>
            <p className="font-bold text-lg" data-testid="dsr-value">
              {Number.isFinite(dsrPct) ? `${dsrPct.toFixed(2)}%` : '—'}{' '}
              <span className={dsrWithinLimit ? 'text-blue-600' : 'text-red-600'}>
                · {dsrWithinLimit ? 'within limit' : 'over limit'}
              </span>
            </p>
            <p className="text-xs text-gray-500">
              {formulatedIncome ? `Income RM ${Number(formulatedIncome).toLocaleString('en-MY')}` : 'Debt service ratio'}
              {maxLoanAmount ? ` · Max RM ${Number(maxLoanAmount).toLocaleString('en-MY')}` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
            aiconBand === 'green' ? 'bg-emerald-100' :
            (aiconBand === 'amber' || aiconBand === 'yellow') ? 'bg-amber-100' :
            aiconBand === 'red' ? 'bg-red-100' : 'bg-purple-100'
          }`}>
            <span className={`text-xs font-bold ${
              aiconBand === 'green' ? 'text-emerald-700' :
              (aiconBand === 'amber' || aiconBand === 'yellow') ? 'text-amber-700' :
              aiconBand === 'red' ? 'text-red-700' : 'text-purple-700'
            }`}>RB</span>
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-wider text-gray-500">Risk Band</p>
            <p className={`font-bold text-lg capitalize ${bandColor}`} data-testid="risk-band-value">
              {bandLabel}
            </p>
            <p className="text-xs text-gray-500">Risk assessment</p>
          </div>
        </div>
      </Card>

      {/* Recommended banks pill list */}
      {recommendedBanks.length > 0 && (
        <Card className="p-4 space-y-2" data-testid="recommended-banks-card">
          <p className="font-semibold text-sm">Recommended banks</p>
          <div className="flex flex-wrap gap-2">
            {recommendedBanks.map((b) => (
              <Badge key={b.bank_code} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                {b.bank_name || b.bank_code}
              </Badge>
            ))}
          </div>
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Higher approval probability
          </p>
        </Card>
      )}

      {/* V4.24.72 — Waiting Score: no picker, no Special Handling banner,
          just a "processing" note. AICon must complete first. */}
      {isWaitingScore && (
        <Card className="p-4 space-y-2 bg-blue-50 border-blue-200 text-blue-900" data-testid="waiting-score-banner">
          <p className="font-semibold flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> AICon is scoring your profile
          </p>
          <p className="text-sm">
            Please complete the Loan Profiling Questionnaire (if not done) and wait for the AICon result.
            Loan-type selection will appear here once your profile is scored.
          </p>
        </Card>
      )}

      {/* V4.24.79 — Phase 1 (AICon): show whenever the case is at
          ready_pick_banker and AICon did NOT explicitly fail. This
          decouples the picker gate from the CCRIS/DSR fields being
          populated — some cases arrive at ready_pick_banker with
          `recommended_banks` but empty score fields; they still deserve
          a picker. */}
      {isPickerAvailable && phase === 'aicon' && (
        <Card className="p-4 space-y-3 bg-emerald-50 border-emerald-200" data-testid="aicon-continue-card">
          <p className="text-sm text-emerald-900">
            Review the CCRIS score, DSR, and recommended banks above. When you&apos;re ready, continue to pick your loan type.
          </p>
          <Button
            className="w-full bg-blue-600 hover:bg-blue-700"
            onClick={() => setPhase('picker')}
            data-testid="aicon-continue-btn"
          >
            Choose selection path →
          </Button>
        </Card>
      )}

      {/* V4.24.76 — Phase 2 (loan-type picker) — matches Mobile design:
          Type B is rendered as a section header with each outsource
          partner as an individual radio in the SAME group as a / c.
          Selecting a partner IS selecting Type B with that partner. */}
      {isPickerAvailable && phase === 'picker' && (
        <Card className="p-4 space-y-3" data-testid="path-picker-card">
          <RadioGroup value={selected === 'B_outsource' && selectedOutsourceId ? `out:${selectedOutsourceId}` : selected}
                      onValueChange={(v) => {
                        if (v.startsWith('out:')) {
                          setSelected('B_outsource');
                          setSelectedOutsourceId(v.slice(4));
                        } else {
                          setSelected(v);
                          setSelectedOutsourceId('');
                        }
                      }}>
            {/* a — Customer's own bank */}
            <label htmlFor="path-A_own_bank"
                   className={`border rounded-lg p-3 flex items-start gap-3 cursor-pointer bg-white ${
                     selected === 'A_own_bank' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:bg-gray-50'
                   }`}
                   data-testid="path-option-A_own_bank">
              <RadioGroupItem value="A_own_bank" id="path-A_own_bank" className="mt-1" />
              <div className="flex-1">
                <p className="font-semibold">a - Customer&apos;s own bank</p>
                <p className="text-xs text-gray-500">No system action · manual update</p>
              </div>
            </label>

            {/* b — Outsource agency (header + inline radio list) */}
            <div className="border rounded-lg p-3 bg-white" data-testid="path-section-B_outsource">
              <p className="font-semibold mb-2">b - Outsource agency</p>
              <div className="space-y-2">
                {outsourcePartners.length === 0 && (
                  <p className="text-xs text-gray-500 italic">Loading outsource partners…</p>
                )}
                {outsourcePartners.map((p) => (
                  <label key={p.id}
                         htmlFor={`out-${p.id}`}
                         className={`flex items-start gap-3 py-2 border-t first:border-t-0 cursor-pointer ${
                           selected === 'B_outsource' && selectedOutsourceId === p.id ? 'text-emerald-700' : ''
                         }`}
                         data-testid={`outsource-partner-${p.id}`}>
                    <RadioGroupItem value={`out:${p.id}`} id={`out-${p.id}`} className="mt-1" />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{p.name}</p>
                      {(p.fee_percent != null || p.monthly_cap) && (
                        <p className="text-xs text-gray-500">
                          {p.fee_percent != null ? `Fee ${p.fee_percent.toFixed(2)}%` : ''}
                          {p.monthly_cap ? ` · Cap ${p.monthly_cap}` : ''}
                        </p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* c — Recommended bank */}
            <label htmlFor="path-C_recommended"
                   className={`border rounded-lg p-3 flex items-start gap-3 cursor-pointer bg-white ${
                     selected === 'C_recommended' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:bg-gray-50'
                   }`}
                   data-testid="path-option-C_recommended">
              <RadioGroupItem value="C_recommended" id="path-C_recommended" className="mt-1" />
              <div className="flex-1">
                <p className="font-semibold flex items-center gap-1">c - Recommended bank <span className="text-emerald-600">★</span></p>
                <p className="text-xs text-gray-500">Best match based on your CCRIS profile</p>
              </div>
            </label>
          </RadioGroup>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setPhase('aicon')} data-testid="path-back-btn">
              ← Back
            </Button>
            <Button
              className="flex-1 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60"
              onClick={() => submit(selected)}
              disabled={submitting || !selected || (selected === 'B_outsource' && !selectedOutsourceId)}
              data-testid="path-continue-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {!selected || (selected === 'B_outsource' && !selectedOutsourceId)
                ? 'Select a bank category'
                : `Continue with ${selected === 'B_outsource'
                    ? (outsourcePartners.find(p => p.id === selectedOutsourceId)?.name || 'Outsource')
                    : (PATH_OPTIONS.find(p => p.id === selected)?.title || 'selection')} →`}
            </Button>
          </div>
        </Card>
      )}

      {/* V4.24.79 — Show Special Handling banner ONLY when AICon
          explicitly returned is_passed=false. Never show it based on
          missing/degraded score fields — that just means AICon hasn't
          filled the score card yet. */}
      {isExplicitFail && (
        <Card className="p-4 space-y-3 bg-amber-50 border-amber-200" data-testid="special-handling-banner">
          <p className="font-semibold text-amber-900 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Special Handling required
          </p>
          <p className="text-sm text-amber-900">
            Based on your CCRIS score and DSR, your loan case needs Special Handling. Our team will assign a dedicated
            officer to work on your application manually. Selection path options are not available for this profile.
          </p>
          <Button
            className="w-full bg-amber-600 hover:bg-amber-700 text-white"
            onClick={() => submit('D_any_bank')}
            disabled={submitting}
            data-testid="special-handling-continue-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Continue as Special Handling →
          </Button>
        </Card>
      )}

      {caseData.status !== 'ready_pick_banker' && !isWaitingScore && (
        <Card className="p-4 bg-blue-50 border-blue-200 text-sm text-blue-900" data-testid="score-status-banner">
          Current status: <b>{caseData.loan_status_label || caseData.status}</b>. Selection path can only be changed at <b>Ready Pick Bank</b>.
        </Card>
      )}
    </div>
  );
}
